import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Edit3, ExternalLink, Globe, Plus, Users } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, fmtMoney, relativeTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardSkeleton, EmptyState, Field, KpiCard, PageHeader, StatusBadge } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ClientForm from "@/components/forms/ClientForm";
import { FormGrid, TextField } from "@/components/forms/Fields";

const BANNERS = {
  Automotive: "https://images.unsplash.com/photo-1581091212991-8891c7d4bd9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxhdXRvbW90aXZlJTIwZW5naW5lZXIlMjBtYW51ZmFjdHVyaW5nfGVufDB8fHx8MTc4Njk2ODAxMHww&ixlib=rb-4.1.0&q=85",
  Semiconductor: "https://images.unsplash.com/photo-1632914146475-bfe6fa6b2a12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxzZW1pY29uZHVjdG9yJTIwY2hpcCUyMGZhY3RvcnklMjB3b3JrZXJ8ZW58MHx8fHwxNzg2OTY4MDEwfDA&ixlib=rb-4.1.0&q=85",
};

export function Clients() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/clients", { params: { search: search || undefined } });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [] });
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Client CRM"
        title="Clients"
        subtitle="Accounts, hiring managers and delivery performance across every engagement."
        actions={
          can("clients", "create") && (
            <Button onClick={() => setOpen(true)} data-testid="add-client-button" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
              <Plus className="h-3.5 w-3.5" /> Add client
            </Button>
          )
        }
      />

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="clients-search-input"
        placeholder="Search by company, industry or location"
        className="h-9 max-w-md border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400"
      />

      {!data ? (
        <CardSkeleton count={6} />
      ) : data.items.length === 0 ? (
        <EmptyState icon={Building2} title="No clients found" description="Add a client account to start raising requirements." testid="clients-empty-state" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.items.map((c) => (
            <article key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid="client-card"
              className="surface surface-hover cursor-pointer overflow-hidden rounded-md">
              <div className="relative h-20">
                <img src={BANNERS[c.industry] || BANNERS.Automotive} alt={`${c.industry} operations`} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/85 to-white/10" />
              </div>
              <div className="flex items-end justify-between gap-3 px-4 pt-3">
                <div className="min-w-0">
                  <h3 className="truncate text-[15px] font-medium text-slate-900">{c.name}</h3>
                  <p className="truncate text-[11px] text-slate-500">{c.industry} · {c.locations?.[0] || "Location not available"}</p>
                </div>
                <StatusBadge value={c.contract_status} />
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 border-t border-slate-200 p-4">
                {[["Open jobs", c.open_jobs], ["Submitted", c.submissions], ["Active", c.active_candidates], ["Hires", c.total_hires]].map(([k, v]) => (
                  <div key={k}>
                    <p className="label-xs">{k}</p>
                    <p className="font-mono-num mt-1 text-lg text-slate-900">{v}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-[11px] text-slate-500">
                <span>{c.account_manager_name || "No account manager"}</span>
                <span className="font-mono-num">{fmtMoney(c.revenue)}</span>
              </div>
            </article>
          ))}
        </div>
      )}

      <ClientForm open={open} onOpenChange={setOpen} onSaved={(c) => navigate(`/clients/${c.id}`)} />
    </div>
  );
}

export function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", designation: "", email: "", phone: "" });
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      setC(data);
    } catch (e) {
      toast.error(apiError(e, "Client not found"));
      navigate("/clients");
    }
  }, [clientId, navigate]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  const addContact = async () => {
    if (!contact.name.trim()) return toast.error("Contact name is required");
    try {
      await api.post(`/clients/${clientId}/contacts`, { ...contact, is_hiring_manager: true });
      toast.success("Contact added");
      setContactOpen(false);
      setContact({ name: "", designation: "", email: "", phone: "" });
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!c)
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 bg-slate-100" />
        <Skeleton className="h-64 bg-slate-100" />
      </div>
    );

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/clients")} data-testid="back-to-clients" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-600">
        <ArrowLeft className="h-3.5 w-3.5" /> All clients
      </button>

      <div className="surface relative overflow-hidden rounded-md">
        <img src={BANNERS[c.industry] || BANNERS.Automotive} alt={`${c.industry} operations`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/25" />
        <div className="relative flex flex-col gap-4 p-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="label-xs">{c.industry}</p>
            <h1 className="mt-1.5 text-2xl font-medium tracking-tight text-slate-900 sm:text-3xl">{c.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-500">
              {c.website && (
                <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-blue-700">
                  <Globe className="h-3 w-3" /> {c.website.replace(/^https?:\/\//, "")}
                </a>
              )}
              <span>{(c.locations || []).join(" · ") || "Locations not available"}</span>
              <StatusBadge value={c.contract_status} />
            </div>
          </div>
          {isStaff && (
            <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-client-button"
              className="h-9 gap-2 rounded-full border-slate-200 bg-white/80 text-xs text-slate-600 hover:bg-slate-100">
              <Edit3 className="h-3.5 w-3.5" /> Edit client
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard label="Open jobs" value={c.open_jobs} testid="client-kpi-jobs" />
        <KpiCard label="Submissions" value={c.submissions} tone="amber" />
        <KpiCard label="Active candidates" value={c.active_candidates} tone="cyan" />
        <KpiCard label="Total hires" value={c.total_hires} tone="emerald" />
        <KpiCard label="Revenue" value={fmtMoney(c.revenue)} tone="slate" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-slate-200 bg-white p-1">
          {["overview", "jobs", "submissions", "interviews", "offers", "hires", "documents", "activity"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`client-tab-${t}`}
              className="rounded px-3.5 py-1.5 text-xs capitalize data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-5 grid gap-4 lg:grid-cols-3">
          <div className="surface space-y-4 rounded-md p-6 lg:col-span-2">
            <p className="label-xs">Account notes</p>
            <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">{c.notes || "Not available"}</p>
            <div className="grid gap-5 border-t border-slate-200 pt-5 sm:grid-cols-3">
              <Field label="Primary contact" value={c.primary_contact_name} />
              <Field label="Email" value={c.email} />
              <Field label="Phone" value={c.phone} mono />
              <Field label="Account manager" value={c.account_manager_name} />
              <Field label="Contract" value={c.contract_status} />
              <Field label="Onboarded" value={fmtDate(c.created_at)} />
            </div>
          </div>
          <div className="surface rounded-md p-6">
            <div className="flex items-center justify-between">
              <p className="label-xs">Hiring managers</p>
              {isStaff && (
                <button onClick={() => setContactOpen(true)} data-testid="add-contact-button" className="text-[11px] text-blue-600 hover:text-blue-700">
                  Add
                </button>
              )}
            </div>
            {(c.contacts || []).length === 0 ? (
              <p className="mt-4 text-xs text-slate-400">No contacts recorded.</p>
            ) : (
              <ul className="mt-4 space-y-4">
                {c.contacts.map((ct) => (
                  <li key={ct.id} data-testid="client-contact">
                    <p className="text-sm text-slate-900">{ct.name}</p>
                    <p className="text-[11px] text-slate-500">{ct.designation || "Designation not available"}</p>
                    <p className="text-[11px] text-slate-400">{ct.email}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="mt-5">
          {(c.jobs || []).length === 0 ? (
            <EmptyState icon={Building2} title="No jobs raised yet" />
          ) : (
            <div className="surface overflow-x-auto rounded-md thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 hover:bg-transparent">
                    {["Job", "Location", "Experience", "Openings", "Priority", "Status"].map((h) => (
                      <TableHead key={h} className="label-xs whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.jobs.map((j) => (
                    <TableRow key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid="client-job-row"
                      className="cursor-pointer border-slate-200 hover:bg-slate-100">
                      <TableCell>
                        <p className="text-sm text-slate-900">{j.title}</p>
                        <p className="font-mono-num text-[10px] text-slate-400">{j.job_code}</p>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{j.location}</TableCell>
                      <TableCell className="font-mono-num text-xs text-slate-500">
                        {j.min_experience ?? "?"}–{j.max_experience ?? "?"} yrs
                      </TableCell>
                      <TableCell className="font-mono-num text-xs text-slate-500">{j.openings}</TableCell>
                      <TableCell><StatusBadge value={j.priority} /></TableCell>
                      <TableCell><StatusBadge value={j.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions" className="mt-5">
          <SubmissionTable rows={c.submissions_list} navigate={navigate} which="submissions" />
        </TabsContent>
        <TabsContent value="hires" className="mt-5">
          <SubmissionTable rows={c.hires} navigate={navigate} which="hires" />
        </TabsContent>

        <TabsContent value="interviews" className="mt-5">
          {(c.interviews || []).length === 0 ? (
            <EmptyState icon={Users} title="No interviews scheduled" />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {c.interviews.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p className="text-sm text-slate-900">{iv.round_name} · {iv.interview_type}</p>
                    <p className="text-[11px] text-slate-500">{fmtDate(iv.scheduled_at)} · {iv.interviewer_name || "Panel not assigned"}</p>
                  </div>
                  <StatusBadge value={iv.status} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="offers" className="mt-5">
          {(c.offers || []).length === 0 ? (
            <EmptyState icon={Users} title="No offers raised" />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {c.offers.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p className="font-mono-num text-sm text-slate-900">{o.offered_ctc} LPA</p>
                    <p className="text-[11px] text-slate-500">Joining {fmtDate(o.joining_date)}</p>
                  </div>
                  <div className="space-y-1.5 text-right">
                    <StatusBadge value={o.status} />
                    {o.joining_status && <StatusBadge value={o.joining_status} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-5">
          {(c.documents || []).length === 0 ? (
            <EmptyState icon={Users} title="No client documents" description="Upload contracts and agreements from the Documents module." />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {c.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p className="text-sm text-slate-800">{d.name}</p>
                    <p className="text-[11px] text-slate-400">{d.doc_type} · {fmtDate(d.created_at)}</p>
                  </div>
                  <StatusBadge value={`v${d.version}`} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-5">
          <div className="surface rounded-md p-6">
            {(c.activity || []).length === 0 ? (
              <p className="text-xs text-slate-400">No activity recorded.</p>
            ) : (
              <ul className="space-y-4">
                {c.activity.map((a) => (
                  <li key={a.id} className="flex gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                    <div>
                      <p className="text-xs text-slate-600"><span className="text-slate-900">{a.user_name}</span> {a.action}</p>
                      <p className="mt-0.5 text-[10px] text-slate-400">{relativeTime(a.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ClientForm open={edit} onOpenChange={setEdit} client={c} onSaved={() => setRefresh((r) => r + 1)} />

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Add a hiring manager</DialogTitle>
          </DialogHeader>
          <FormGrid>
            <TextField label="Name" value={contact.name} onChange={(v) => setContact((c2) => ({ ...c2, name: v }))} testid="contact-name-input" required />
            <TextField label="Designation" value={contact.designation} onChange={(v) => setContact((c2) => ({ ...c2, designation: v }))} />
            <TextField label="Email" value={contact.email} onChange={(v) => setContact((c2) => ({ ...c2, email: v }))} />
            <TextField label="Phone" value={contact.phone} onChange={(v) => setContact((c2) => ({ ...c2, phone: v }))} />
          </FormGrid>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContactOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={addContact} data-testid="contact-submit-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Add contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubmissionTable({ rows = [], navigate, which }) {
  if (!rows.length)
    return <EmptyState icon={Users} title={which === "hires" ? "No hires yet" : "No candidates submitted yet"} />;
  return (
    <div className="surface overflow-x-auto rounded-md thin-scroll">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-200 hover:bg-transparent">
            {["Candidate", "Job", "Experience", "Location", "Match", "Stage"].map((h) => (
              <TableHead key={h} className="label-xs whitespace-nowrap">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.application_id} onClick={() => navigate(`/candidates/${r.candidate_id}`)}
              data-testid="client-submission-row" className="cursor-pointer border-slate-200 hover:bg-slate-100">
              <TableCell>
                <p className="text-sm text-slate-900">{r.name}</p>
                <p className="text-[11px] text-slate-500">{r.designation || "Role not available"}</p>
              </TableCell>
              <TableCell className="text-xs text-slate-500">{r.job_title}</TableCell>
              <TableCell className="font-mono-num text-xs text-slate-500">{r.experience ?? "?"} yrs</TableCell>
              <TableCell className="text-xs text-slate-500">{r.location?.split(",")[0] || "—"}</TableCell>
              <TableCell className="font-mono-num text-xs text-slate-600">{r.match_score ?? "—"}%</TableCell>
              <TableCell><StatusBadge value={r.stage} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
